import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Copy, Check, X, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

import { RootState } from '../../../reducers';
import { clearVisibleToken } from '../settingsSlice';

const VisibleToken: React.FC<{ tokenId: string }> = ({ tokenId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const tokenInfo = useSelector(
    (state: RootState) => state.settings.api.tokens.visible[tokenId],
  );

  const handleCopy = async () => {
    if (tokenInfo?.token) {
      try {
        await navigator.clipboard.writeText(tokenInfo.token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy token:', error);
      }
    }
  };

  const handleClose = () => {
    dispatch(clearVisibleToken(tokenId));
  };

  if (!tokenInfo) {
    return null;
  }

  return (
    <Card className="my-4 border border-border rounded-lg relative">
      <CardContent className="p-4">
        {tokenInfo.status === 'succeeded' ? (
          <TooltipProvider>
            <Alert className="border-green-200 bg-green-50">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-medium">
                  {t('settings.api.visibleToken.success.title')}
                </h3>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClose}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('settings.api.visibleToken.tooltips.close')}
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="flex items-center mb-2 gap-2">
                <code className="font-mono flex-1 bg-background p-2 rounded text-sm">
                  {showToken ? tokenInfo.token : '•'.repeat(20)}
                </code>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowToken(!showToken)}
                      className="h-8 w-8 p-0"
                    >
                      {showToken ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showToken
                      ? t('settings.api.visibleToken.tooltips.hideToken')
                      : t('settings.api.visibleToken.tooltips.showToken')}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopy}
                      className={`h-8 w-8 p-0 ${copied ? 'text-green-600' : ''}`}
                    >
                      {copied ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {copied
                      ? t('settings.api.visibleToken.tooltips.copied')
                      : t('settings.api.visibleToken.tooltips.copyToken')}
                  </TooltipContent>
                </Tooltip>
              </div>

              <AlertDescription className="mt-2">
                {t('settings.api.visibleToken.success.warning')}
              </AlertDescription>
            </Alert>
          </TooltipProvider>
        ) : (
          <TooltipProvider>
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-medium">
                {t('settings.api.visibleToken.tokenInfo.title', {
                  name: tokenInfo.tokenInfo?.name,
                })}
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('settings.api.visibleToken.tooltips.close')}
                </TooltipContent>
              </Tooltip>
            </div>

            {tokenInfo.status === 'failed' && (
              <Alert variant="destructive" className="mt-2">
                <AlertDescription>
                  {t('settings.api.visibleToken.errors.failed', {
                    operation: tokenInfo.operation,
                  })}
                  <div className="mt-1 text-sm break-words">
                    {t('settings.api.visibleToken.errors.error', {
                      message: tokenInfo.error,
                    })}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
};

export default VisibleToken;
